import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../lib/api';
import { fmt$, fmtDate, s } from './reportKit';

// ── Owner Metrics Tab (async, mirrors ReplenishmentTab) ──
// state: { status: 'idle' | 'running' | 'done' | 'error', result?, days, ... }
export default function OwnerMetricsReport() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState('');
  const [days, setDays] = useState(90);
  const [mrrMode, setMrrMode] = useState('all');
  const [customerSort, setCustomerSort] = useState('revenue');
  const [customerFilter, setCustomerFilter] = useState('');
  const [kickingOff, setKickingOff] = useState(false);

  const load = useCallback(async (d) => {
    setLoading(true);
    setError('');
    try {
      const result = await adminApi.getOwnerMetrics({ days: String(d) });
      setState(result);
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(days); }, [load, days]);

  useEffect(() => {
    if (state?.status !== 'running') { setPolling(false); return; }
    setPolling(true);
    let cancelled = false;
    const id = setInterval(async () => {
      try {
        const next = await adminApi.getOwnerMetricsStatus({ days: String(days) });
        if (cancelled) return;
        if (next?.status === 'running') {
          setState(prev => prev ? { ...prev, ...next } : next);
        } else {
          clearInterval(id);
          const full = await adminApi.getOwnerMetrics({ days: String(days) });
          if (!cancelled) setState(full);
        }
      } catch (e) {
        console.warn('Owner metrics poll failed:', e.message);
      }
    }, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [state?.status, days]);

  async function kickoffRefresh() {
    setKickingOff(true);
    setError('');
    try {
      await adminApi.refreshOwnerMetrics({ days: String(days) });
      const next = await adminApi.getOwnerMetricsStatus({ days: String(days) });
      setState(next);
    } catch (e) {
      setError(e.message);
    } finally {
      setKickingOff(false);
    }
  }

  if (loading) return <p style={s.sub}>Loading owner metrics…</p>;
  if (error) return (
    <div style={{ textAlign: 'center', padding: '2rem' }}>
      <p style={s.errorText}>{error}</p>
      <button style={{ ...s.btnPrimary, marginTop: '1rem' }} onClick={() => load(days)}>Retry</button>
    </div>
  );

  const status = state?.status || 'idle';
  const periodSelector = (
    <div style={s.rangeBar}>
      {[30, 60, 90, 180].map(d => (
        <button key={d} style={days === d ? { ...s.rangeBtn, ...s.rangeBtnActive } : s.rangeBtn} onClick={() => setDays(d)}>
          {d}d
        </button>
      ))}
    </div>
  );

  if (status === 'idle') return (
    <div>
      <div style={{ marginBottom: '1rem' }}>{periodSelector}</div>
      <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
        <h3 style={{ ...s.sectionTitle, marginBottom: '0.5rem' }}>No report yet for {days}-day window</h3>
        <p style={s.sub}>
          First run pulls every order in the window — typically 1–3 minutes for {days}d.
          Once computed, switching back to this window loads instantly.
        </p>
        <button style={{ ...s.btnPrimary, marginTop: '1.25rem' }} onClick={kickoffRefresh} disabled={kickingOff}>
          {kickingOff ? 'Starting…' : `Compute ${days}-day Report`}
        </button>
      </div>
    </div>
  );

  if (status === 'running') {
    const startedAt = state.startedAt ? new Date(state.startedAt) : null;
    const elapsedSec = startedAt ? Math.round((Date.now() - startedAt.getTime()) / 1000) : 0;
    return (
      <div>
        <div style={{ marginBottom: '1rem' }}>{periodSelector}</div>
        <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
          <h3 style={s.sectionTitle}>Computing {days}-day owner metrics…</h3>
          <p style={s.sub}>
            Fetching every sales order in the window from SOS. Typically 1–3 minutes.
            {elapsedSec > 0 && ` Running for ${elapsedSec}s.`}
          </p>
          <div style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#aaa' }}>
            {polling ? 'Polling every 5 seconds…' : 'Checking…'}
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') return (
    <div>
      <div style={{ marginBottom: '1rem' }}>{periodSelector}</div>
      <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
        <h3 style={{ ...s.sectionTitle, color: '#c62828' }}>Computation failed</h3>
        <p style={{ ...s.errorText, marginBottom: '1rem' }}>{state.error || 'Unknown error'}</p>
        <button style={s.btnPrimary} onClick={kickoffRefresh} disabled={kickingOff}>
          {kickingOff ? 'Retrying…' : 'Try again'}
        </button>
      </div>
    </div>
  );

  // status === 'done'
  const data = state.result;
  if (!data) return null;
  const { summary, topCustomers, topProductsByQty, topProductsByDollar, lapsed, onlineVsOffline, revenuePerCustomer, mrr, churn, slowMoving, grossProfit, replenishmentAvailable } = data;

  const mrrValue = mrr ? (mrrMode === 'repeat' ? mrr.repeatCustomers : mrr.allCustomers) : 0;
  const mrrModeLabels = {
    all:    `All Customers · last ${days}d ÷ ${(days/30).toFixed(1)}mo`,
    repeat: `Repeat customers (≥2 orders) · last ${days}d ÷ ${(days/30).toFixed(1)}mo`,
  };

  const filteredRevPerCustomer = (revenuePerCustomer || [])
    .filter(c => !customerFilter || (c.customerName || '').toLowerCase().includes(customerFilter.toLowerCase()))
    .sort((a, b) => {
      if (customerSort === 'revenue') return (b.totalRevenue || 0) - (a.totalRevenue || 0);
      if (customerSort === 'orders')  return (b.orderCount || 0)   - (a.orderCount || 0);
      if (customerSort === 'last')    return (b.lastOrderDate || '').localeCompare(a.lastOrderDate || '');
      return 0;
    });

  const lapsedTiles = [
    { key: 'active7d',     label: 'Active (≤7d)',                count: lapsed.active7d,     color: '#2e7d32' },
    { key: 'lapsed7to30',  label: 'Lapsed 7–30 days',            count: lapsed.lapsed7to30,  color: '#e65100', sample: lapsed.sample?.lapsed7to30 },
    { key: 'lapsed30to90', label: 'Lapsed 30–90 days',           count: lapsed.lapsed30to90, color: '#c62828', sample: lapsed.sample?.lapsed30to90 },
    { key: 'lapsed90plus', label: 'Lapsed >90 days',             count: lapsed.lapsed90plus, color: '#7b1fa2', sample: lapsed.sample?.lapsed90plus },
    { key: 'neverOrdered', label: `No order in last ${days}d`,   count: lapsed.neverOrdered, color: '#888' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        {periodSelector}
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <div style={{ fontSize: '0.82rem', color: '#888' }}>
            As of {state.finishedAt ? new Date(state.finishedAt).toLocaleString() : '—'}
          </div>
          <button style={s.btnOutline} onClick={kickoffRefresh} disabled={kickingOff}>
            {kickingOff ? 'Starting…' : 'Recompute'}
          </button>
        </div>
      </div>

      {summary.sampled && summary.warnings?.length > 0 && (
        <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#664d03' }}>
          <strong>Heads up — some SOS pages failed to load:</strong>
          <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.25rem' }}>
            {summary.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      <div style={s.kpiGrid}>
        <div style={s.kpiCard}><div style={s.kpiLabel}>Revenue ({days}d)</div><div style={s.kpiValue}>{fmt$(summary.totalRevenue)}</div></div>
        <div style={s.kpiCard}><div style={s.kpiLabel}>Avg Order Value</div><div style={s.kpiValue}>{fmt$(summary.avgOrderValue)}</div></div>
        <div style={s.kpiCard}><div style={s.kpiLabel}>Ordering Customers</div><div style={s.kpiValue}>{summary.uniqueOrderingCustomers}</div><div style={{ ...s.kpiLabel, marginTop: 4, marginBottom: 0 }}>of {summary.totalCustomers} total</div></div>
        <div style={s.kpiCard}><div style={s.kpiLabel}>Total Orders</div><div style={s.kpiValue}>{summary.totalOrders}</div></div>
      </div>

      <div style={s.twoCol}>
        <div style={s.section}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            <h3 style={{ ...s.sectionTitle, margin: 0 }}>Monthly Recurring Revenue</h3>
            <div style={s.rangeBar}>
              {[['all', 'All'], ['repeat', 'Repeat']].map(([k, label]) => (
                <button key={k} style={mrrMode === k ? { ...s.rangeBtn, ...s.rangeBtnActive } : s.rangeBtn} onClick={() => setMrrMode(k)}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ ...s.kpiValue, fontSize: '2rem' }}>{fmt$(mrrValue)}</div>
          <div style={{ ...s.kpiLabel, marginTop: 8, marginBottom: 0 }}>{mrrModeLabels[mrrMode]}</div>
        </div>

        <div style={s.section}>
          <h3 style={s.sectionTitle}>Customer Churn Rate</h3>
          {churn && churn.priorPeriodCustomers > 0 ? (
            <>
              <div style={{ ...s.kpiValue, fontSize: '2rem', color: (churn.churnRate || 0) > 0.3 ? '#c62828' : '#333' }}>
                {(churn.churnRate * 100).toFixed(1)}%
              </div>
              <div style={{ ...s.kpiLabel, marginTop: 8, marginBottom: 0 }}>
                {churn.churnedCustomers} churned · {churn.retainedCustomers} retained · of {churn.priorPeriodCustomers} active 90–180d ago
              </div>
            </>
          ) : (
            <div style={{ fontSize: '0.85rem', color: '#888', padding: '1rem 0' }}>
              {days < 180 ? 'Churn needs the 180-day window — switch to 180d above to compute.' : 'No customers active 90–180 days ago.'}
            </div>
          )}
        </div>
      </div>

      <div style={s.section}>
        <h3 style={s.sectionTitle}>Customer Activity</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
          {lapsedTiles.map(t => (
            <div key={t.key} style={{ ...s.kpiCard, borderTop: `3px solid ${t.color}` }}>
              <div style={s.kpiLabel}>{t.label}</div>
              <div style={{ ...s.kpiValue, color: t.color }}>{t.count}</div>
              {t.sample && t.sample.length > 0 && (
                <div style={{ fontSize: '0.72rem', color: '#888', marginTop: 4, lineHeight: 1.35 }}>
                  {t.sample.join(', ')}{t.count > t.sample.length && ` +${t.count - t.sample.length} more`}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div style={s.section}>
        <h3 style={s.sectionTitle}>Online vs Offline (in last {days}d)</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
          <div style={s.kpiCard}><div style={s.kpiLabel}>Portal only</div><div style={{ ...s.kpiValue, color: '#2e7d32' }}>{onlineVsOffline.onlineOnly}</div></div>
          <div style={s.kpiCard}><div style={s.kpiLabel}>Mixed</div><div style={{ ...s.kpiValue, color: '#e65100' }}>{onlineVsOffline.mixed}</div></div>
          <div style={s.kpiCard}><div style={s.kpiLabel}>Manual only</div><div style={{ ...s.kpiValue, color: '#c62828' }}>{onlineVsOffline.offlineOnly}</div></div>
          <div style={s.kpiCard}><div style={s.kpiLabel}>No orders</div><div style={{ ...s.kpiValue, color: '#888' }}>{onlineVsOffline.neverOrdered}</div></div>
        </div>
      </div>

      {!replenishmentAvailable && (
        <div style={s.section}>
          <div style={{ background: '#e3f2fd', border: '1px solid #1565c0', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.88rem', color: '#0d47a1' }}>
            <strong>Slow Moving + Gross Profit need the replenishment report.</strong> Open <strong>purchase.silverservice.com</strong> and click "Refresh from SOS" — takes ~2 min — then reload this tab.
          </div>
        </div>
      )}

      {grossProfit && (
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Gross Profit per Order</h3>
          <div style={s.kpiGrid}>
            <div style={s.kpiCard}><div style={s.kpiLabel}>Avg GP / Order</div><div style={s.kpiValue}>{fmt$(grossProfit.avgGpPerOrder)}</div></div>
            <div style={s.kpiCard}><div style={s.kpiLabel}>GP Margin</div><div style={{ ...s.kpiValue, color: grossProfit.avgGpMarginPct < 0.2 ? '#c62828' : '#2e7d32' }}>{(grossProfit.avgGpMarginPct * 100).toFixed(1)}%</div></div>
            <div style={s.kpiCard}><div style={s.kpiLabel}>Total GP ({days}d)</div><div style={s.kpiValue}>{fmt$(grossProfit.totalGp)}</div></div>
            <div style={s.kpiCard}><div style={s.kpiLabel}>Orders Priced</div><div style={s.kpiValue}>{grossProfit.countableOrders}</div><div style={{ ...s.kpiLabel, marginTop: 4, marginBottom: 0 }}>of {summary.totalOrders}</div></div>
          </div>
          {grossProfit.topOrders && grossProfit.topOrders.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#555', marginBottom: '0.5rem' }}>Top 20 orders by gross profit</div>
              <table style={s.table}>
                <thead><tr><th style={s.th}>Order #</th><th style={s.th}>Date</th><th style={s.th}>Customer</th><th style={s.thR}>Revenue</th><th style={s.thR}>Cost</th><th style={s.thR}>GP</th><th style={s.thR}>Margin</th></tr></thead>
                <tbody>
                  {grossProfit.topOrders.map((o, i) => (
                    <tr key={o.id || i}>
                      <td style={s.td}>{o.number}</td>
                      <td style={s.td}>{fmtDate(o.date)}</td>
                      <td style={s.td}>{o.customerName}</td>
                      <td style={s.tdR}>{fmt$(o.revenue)}</td>
                      <td style={s.tdR}>{fmt$(o.cost)}</td>
                      <td style={s.tdR}>{fmt$(o.gp)}</td>
                      <td style={s.tdR}>{(o.gpMargin * 100).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {slowMoving && slowMoving.items?.length > 0 && (
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Slow Moving Items (top 50 by $ tied up)</h3>
          <p style={s.sub}>Items with on-hand stock and less than {slowMoving.monthlyThreshold} unit{slowMoving.monthlyThreshold === 1 ? '' : 's'}/month avg sales.</p>
          <table style={s.table}>
            <thead><tr><th style={s.th}>Rank</th><th style={s.th}>Item</th><th style={s.th}>Category</th><th style={s.thR}>On Hand</th><th style={s.thR}>Mo Avg</th><th style={s.thR}>Mos of Stock</th><th style={s.thR}>$ Tied Up</th></tr></thead>
            <tbody>
              {slowMoving.items.map(it => (
                <tr key={it.itemId}>
                  <td style={s.td}>{it.rank}</td>
                  <td style={s.td}><div>{it.name}</div><div style={{ fontSize: '0.72rem', color: '#888' }}>{it.sku}</div></td>
                  <td style={s.td}>{it.category}</td>
                  <td style={s.tdR}>{it.onHand}</td>
                  <td style={s.tdR}>{(it.avgMonthly || 0).toFixed(1)}</td>
                  <td style={s.tdR}>{it.monthsOfStock == null ? '∞' : it.monthsOfStock.toFixed(0)}</td>
                  <td style={s.tdR}>{fmt$(it.dollarsOnHand)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={s.twoCol}>
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Top 20 Customers by Revenue</h3>
          <table style={s.table}>
            <thead><tr><th style={s.th}>#</th><th style={s.th}>Customer</th><th style={s.thR}>Orders</th><th style={s.thR}>Revenue</th></tr></thead>
            <tbody>
              {topCustomers.map((c, i) => (
                <tr key={c.customerId || i}><td style={s.td}>{i + 1}</td><td style={s.td}>{c.customerName}</td><td style={s.tdR}>{c.orderCount}</td><td style={s.tdR}>{fmt$(c.totalRevenue)}</td></tr>
              ))}
              {topCustomers.length === 0 && <tr><td colSpan={4} style={s.td}>No data</td></tr>}
            </tbody>
          </table>
        </div>

        <div style={s.section}>
          <h3 style={s.sectionTitle}>Top 20 Products by $ Revenue</h3>
          <table style={s.table}>
            <thead><tr><th style={s.th}>#</th><th style={s.th}>Product</th><th style={s.thR}>Qty</th><th style={s.thR}>Revenue</th></tr></thead>
            <tbody>
              {topProductsByDollar.map((p, i) => (
                <tr key={p.itemId || i}><td style={s.td}>{i + 1}</td><td style={s.td}>{p.itemName}</td><td style={s.tdR}>{p.totalQty}</td><td style={s.tdR}>{fmt$(p.totalRevenue)}</td></tr>
              ))}
              {topProductsByDollar.length === 0 && <tr><td colSpan={4} style={s.td}>No data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div style={s.section}>
        <h3 style={s.sectionTitle}>Top 20 Products by Quantity</h3>
        <table style={s.table}>
          <thead><tr><th style={s.th}>#</th><th style={s.th}>Product</th><th style={s.thR}>Qty</th><th style={s.thR}>Revenue</th></tr></thead>
          <tbody>
            {topProductsByQty.map((p, i) => (
              <tr key={p.itemId || i}><td style={s.td}>{i + 1}</td><td style={s.td}>{p.itemName}</td><td style={s.tdR}>{p.totalQty}</td><td style={s.tdR}>{fmt$(p.totalRevenue)}</td></tr>
            ))}
            {topProductsByQty.length === 0 && <tr><td colSpan={4} style={s.td}>No data</td></tr>}
          </tbody>
        </table>
      </div>

      <div style={s.section}>
        <h3 style={s.sectionTitle}>Revenue per Customer ({filteredRevPerCustomer.length})</h3>
        <div style={s.filterBar}>
          <input
            style={{ ...s.input, flex: 1, minWidth: 200 }}
            placeholder="Filter by customer name…"
            value={customerFilter}
            onChange={e => setCustomerFilter(e.target.value)}
          />
          <div style={s.rangeBar}>
            {[['revenue', 'By Revenue'], ['orders', 'By Orders'], ['last', 'By Last Order']].map(([k, label]) => (
              <button key={k} style={customerSort === k ? { ...s.rangeBtn, ...s.rangeBtnActive } : s.rangeBtn} onClick={() => setCustomerSort(k)}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <table style={s.table}>
          <thead><tr><th style={s.th}>Customer</th><th style={s.thR}>Orders</th><th style={s.thR}>Revenue</th><th style={s.thR}>Last Order</th></tr></thead>
          <tbody>
            {filteredRevPerCustomer.slice(0, 500).map(c => (
              <tr key={c.customerId}>
                <td style={s.td}>{c.customerName}</td>
                <td style={s.tdR}>{c.orderCount}</td>
                <td style={s.tdR}>{fmt$(c.totalRevenue)}</td>
                <td style={s.tdR}>{c.lastOrderDate ? fmtDate(c.lastOrderDate) : '—'}</td>
              </tr>
            ))}
            {filteredRevPerCustomer.length === 0 && <tr><td colSpan={4} style={s.td}>No matching customers</td></tr>}
          </tbody>
        </table>
        {filteredRevPerCustomer.length > 500 && (
          <p style={{ ...s.sub, marginTop: '0.5rem' }}>Showing first 500. Filter to narrow.</p>
        )}
      </div>
    </div>
  );
}
